<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { Markdown } from 'tiptap-markdown'
import Button from 'primevue/button'
import ButtonGroup from 'primevue/buttongroup'

const props = defineProps({
    modelValue: {
        type: String,
        default: '',
    },
    placeholder: {
        type: String,
        default: '',
    },
    autosave: Boolean,
    autosaveDelay: {
        type: Number,
        default: 2000,
    },
    fill: Boolean,
})

const emit = defineEmits(['update:modelValue', 'save'])

const previewing = ref(false)
let autosaveTimer = null

const editor = useEditor({
    content: props.modelValue,
    extensions: [
        StarterKit,
        Placeholder.configure({ placeholder: props.placeholder }),
        Markdown.configure({ html: false }),
    ],
    onUpdate: ({ editor: instance }) => {
        const markdown = instance.storage.markdown.getMarkdown()
        emit('update:modelValue', markdown)

        if (props.autosave) {
            clearTimeout(autosaveTimer)
            autosaveTimer = setTimeout(() => emit('save', markdown), props.autosaveDelay)
        }
    },
})

watch(() => props.modelValue, (value) => {
    if (!editor.value) {
        return
    }
    const current = editor.value.storage.markdown.getMarkdown()
    if (value !== current) {
        editor.value.commands.setContent(value ?? '', false)
    }
})

const previewHtml = computed(() => editor.value?.getHTML() ?? '')

function save() {
    clearTimeout(autosaveTimer)
    emit('save', editor.value?.storage.markdown.getMarkdown() ?? '')
}

defineExpose({ save })

onBeforeUnmount(() => {
    clearTimeout(autosaveTimer)
    editor.value?.destroy()
})
</script>

<template>
    <div
        class="border rounded-lg dark:border-gray-700 bg-white dark:bg-gray-900"
        :class="fill ? 'h-full flex flex-col min-h-0' : ''"
    >
        <div class="flex items-center justify-between gap-2 px-2 py-1.5 border-b dark:border-gray-700 shrink-0">
            <ButtonGroup v-if="!previewing">
                <Button size="small" text icon="bi bi-type-h2" @click="editor.chain().focus().toggleHeading({ level: 2 }).run()" />
                <Button size="small" text icon="bi bi-type-bold" @click="editor.chain().focus().toggleBold().run()" />
                <Button size="small" text icon="bi bi-type-italic" @click="editor.chain().focus().toggleItalic().run()" />
                <Button size="small" text icon="pi pi-list" @click="editor.chain().focus().toggleBulletList().run()" />
                <Button size="small" text icon="bi bi-list-ol" @click="editor.chain().focus().toggleOrderedList().run()" />
                <Button size="small" text icon="pi pi-code" @click="editor.chain().focus().toggleCodeBlock().run()" />
            </ButtonGroup>
            <div v-else></div>

            <Button
                v-if="false"
                size="small"
                text
                :icon="previewing ? 'pi pi-pencil' : 'pi pi-eye'"
                :label="previewing ? $t('common.edit') : $t('common.preview')"
                @click="previewing = !previewing"
            />
        </div>

        <div class="p-3" :class="fill ? 'flex-1 min-h-0 overflow-y-auto' : ''">
            <EditorContent v-show="!previewing" :editor="editor" class="markdown-content text-sm focus:outline-none h-full" />
            <div v-if="previewing" class="markdown-content text-sm" v-html="previewHtml"></div>
        </div>

        <div v-if="autosave" class="px-3 pb-2 flex items-center gap-1 text-xs text-neutral-400 shrink-0">
            <i class="pi pi-check-circle !text-[11px]"></i>
            {{ $t('common.autosave_hint') }}
        </div>
    </div>
</template>

<style scoped>
:deep(.ProseMirror) {
    outline: none;
    min-height: 4rem;
}

:deep(.ProseMirror p.is-editor-empty:first-child::before) {
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
    @apply text-neutral-400;
}

.markdown-content:deep(h1),
.markdown-content:deep(h2),
.markdown-content:deep(h3) {
    @apply font-semibold mt-3 mb-1.5;
}

.markdown-content:deep(h1) { @apply text-lg; }
.markdown-content:deep(h2) { @apply text-base; }
.markdown-content:deep(h3) { @apply text-sm; }

.markdown-content:deep(p) {
    @apply mb-2 leading-relaxed;
}

.markdown-content:deep(ul) {
    @apply list-disc pl-5 mb-2;
}

.markdown-content:deep(ol) {
    @apply list-decimal pl-5 mb-2;
}

.markdown-content:deep(code) {
    @apply bg-neutral-100 dark:bg-neutral-800 rounded px-1 py-0.5 text-xs;
}

.markdown-content:deep(pre) {
    @apply bg-neutral-100 dark:bg-neutral-800 rounded p-2 mb-2 overflow-x-auto;
}

.markdown-content:deep(pre code) {
    @apply bg-transparent p-0;
}
</style>
